import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { ParticleField } from "../effects/ParticleField";
import { MaskedLines } from "../Reveal";
import { HERO, IMAGES } from "../../data/content";
import { useLang } from "../../i18n/LanguageContext";
import heroIntroVideo from "../../../assets/davinci_the_scene_begins_almost_completely_still__with_a_s copy.mp4";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const artY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const artScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // Mouse parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 });
  const sy = useSpring(my, { stiffness: 40, damping: 20 });
  const px = useTransform(sx, [-0.5, 0.5], [-24, 24]);
  const py = useTransform(sy, [-0.5, 0.5], [-18, 18]);
  const pxSlow = useTransform(sx, [-0.5, 0.5], [12, -12]);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const onMove = (e) => {
      mx.set(e.clientX / window.innerWidth - 0.5);
      my.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-[#02060D]"
    >
      {/* Artwork layer */}
      <motion.div style={{ y: artY, scale: artScale }} className="absolute inset-0">
        <motion.video
          src={heroIntroVideo}
          aria-label="Cinematic intro scene"
          style={{ x: px, y: py }}
          className="absolute inset-0 h-full w-full object-cover object-[50%_25%] opacity-90"
          autoPlay
          muted
          playsInline
          preload="metadata"
          poster={IMAGES.hero}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#02060D] via-[#02060D]/40 to-[#02060D]/70" />
        <div className="absolute inset-0 bg-[#02060D]/30" />
      </motion.div>

      {/* Breathing glow behind sphere */}
      <motion.div
        style={{ x: pxSlow }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[40vw] w-[40vw] rounded-full bg-[#3B82F6]/25 blur-[120px] animate-breathe pointer-events-none"
      />
      <div className="absolute left-1/2 top-[52%] -translate-x-1/2 h-[16vw] w-[16vw] rounded-full bg-[#F59E0B]/20 blur-[90px] animate-breathe pointer-events-none" />

      {/* Particles */}
      <div className="absolute inset-0">
        <ParticleField density={90} />
      </div>

      {/* Letterbox bars */}
      <div className="absolute top-0 inset-x-0 h-[6vh] bg-gradient-to-b from-black to-transparent pointer-events-none z-10" />
      <div className="absolute bottom-0 inset-x-0 h-[14vh] bg-gradient-to-t from-[#02060D] to-transparent pointer-events-none z-10" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-20 mx-auto max-w-7xl px-6 md:px-12 min-h-screen flex flex-col justify-center pt-20"
      >
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="font-mono text-xs tracking-[0.28em] uppercase text-[#60A5FA] flex items-center gap-3"
        >
          <span className="inline-block h-px w-8 bg-[#60A5FA]/50" />
          {t(HERO.eyebrow)}
        </motion.span>

        <MaskedLines
          className="mt-6 max-w-4xl"
          delay={0.9}
          lineClassName="text-5xl sm:text-7xl md:text-8xl font-medium tracking-tighter leading-[0.98]"
          lines={[
            <span key="a" className="text-white">{t(HERO.h1a)}</span>,
            <span key="b" className="grad-blue-orange">{t(HERO.h1b)}</span>,
          ]}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-8 max-w-xl text-lg sm:text-xl text-white/60 leading-relaxed"
        >
          {t(HERO.sub)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full bg-[#3B82F6] px-7 py-3.5 text-sm font-medium text-white transition-all duration-300 shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:shadow-[0_0_45px_rgba(59,130,246,0.7)] hover:bg-[#60A5FA] active:scale-[0.98]"
          >
            {t(HERO.cta)}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-7 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-white/10 hover:border-white/30 active:scale-[0.98]"
          >
            {t(HERO.cta2)}
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2 }}
          data-testid="hero-credibility-line"
          className="mt-8 font-mono text-xs tracking-[0.15em] uppercase text-white/35"
        >
          {t(HERO.credibility)}
        </motion.p>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{t(HERO.scroll)}</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown className="h-4 w-4 text-white/40" />
        </motion.div>
      </motion.div>
    </section>
  );
};
